import React, { useState } from 'react';
import {
  viewportSafeAreaInsetBottom,
  retrieveLaunchParams
} from '@telegram-apps/sdk-react';

import usePolicy from '@/hooks/usePolicy.ts';
import { useMessagesStore } from '@/store/useMessagesStore.ts';
import UIConfig from '@/UIConfig.ts';
import { MessageUI } from '@/api/messaging/types.ts';
import { ReplyMessage } from '@/components/Messanger/Message/ReplyMessage/ReplyMessage.tsx';
import { BaitBottomBar } from '@/pages/Messenger/InputArea/BaitBottomBar.tsx';
import PendingGiftBottomBar from '@/pages/Messenger/InputArea/PendingGiftBottomBar.tsx';
import InsufficientDataBottomBar from '@/pages/Messenger/InputArea/InsufficientDataBottomBar.tsx';
import MessageInputBottomBar from '@/pages/Messenger/InputArea/MessageInputBottomBar.tsx';

interface InputAreaProps {
  isSystem?: boolean;
  hasMessages: boolean;
  isPendingGift: boolean;
  isBait: boolean;
  contactId: string;
  contactName?: string;
  setIsGiftAccepted: (isAccepted: boolean) => void;
  textAreaRef?: React.RefObject<HTMLTextAreaElement>;
  lastMessageRef?: React.MutableRefObject<HTMLDivElement | null>;
  onFocus: () => void;
  onBlur: () => void;
  handleSend: () => Promise<void>;
  offset: number;
  barHeight: number;
  replyToMessage?: MessageUI | null;
  onCancelReply?: () => void;
  onReplyClick?: () => void;
}

const InputArea: React.FC<InputAreaProps> = ({
  isSystem = false,
  hasMessages = true,
  isPendingGift,
  isBait,
  contactId,
  contactName,
  setIsGiftAccepted,
  textAreaRef,
  onFocus,
  onBlur,
  handleSend,
  offset,
  barHeight,
  replyToMessage,
  onCancelReply,
  onReplyClick
}) => {
  const { isInsufficientData } = usePolicy();
  const { inputMessages, saveInputMessage } = useMessagesStore();
  const [isSending, setIsSending] = useState(false);
  const safeAreaBottom = viewportSafeAreaInsetBottom();

  const { tgWebAppPlatform } = retrieveLaunchParams();
  const isAndroid = tgWebAppPlatform === 'android';
  const isKeyboardOpen = offset > 0;
  // Add Extra space for androids when keyboard is open
  const bottomPadding =
    isAndroid && isKeyboardOpen ? safeAreaBottom + 25 : safeAreaBottom;

  const currentMessage = inputMessages[contactId] || '';

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    saveInputMessage(contactId, e.target.value);
  };

  const isDisabled = !currentMessage.trim() || isSending;

  const onSendClick = async () => {
    if (isDisabled) return;

    setIsSending(true);
    try {
      await handleSend();
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: isPendingGift ? 0 : 15,
        transform: `translateY(-${offset}px)`,
        padding: '8px',
        paddingBottom: bottomPadding,
        borderTop: isBait ? 'none' : '1px solid #ddd',
        background: isBait ? 'none' : UIConfig.Chat.shared.backgroundColor,
        display: 'flex',
        flexDirection: 'column',
        minHeight: barHeight,
        boxSizing: 'border-box',
        zIndex: 10
      }}
    >
      {replyToMessage && (
        <ReplyMessage
          contactName={contactName}
          replyMessage={replyToMessage}
          onReplyClick={onReplyClick}
          onCancelReply={onCancelReply}
          showCancelButton={true}
        />
      )}

      <div
        style={{
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            height: offset,
            background: isBait ? 'none' : UIConfig.Chat.shared.backgroundColor
          }}
        />

        {(() => {
          if (!hasMessages) return null;

          if (isBait) {
            return <BaitBottomBar />;
          }

          if (isInsufficientData) {
            return <InsufficientDataBottomBar />;
          }

          if (isPendingGift) {
            return (
              <PendingGiftBottomBar
                contactId={contactId}
                contactName={contactName}
                onGiftAccepted={() => setIsGiftAccepted(true)}
              />
            );
          }

          return (
            <MessageInputBottomBar
              value={currentMessage}
              onChange={handleChange}
              onSend={onSendClick}
              isDisabled={isDisabled}
              isSystem={isSystem}
              textAreaRef={textAreaRef}
              onFocus={onFocus}
              onBlur={onBlur}
            />
          );
        })()}
      </div>
    </div>
  );
};

export default InputArea;
